/*
 * Roster tab — Attendance › Roster (Phase-3 C1, expanded v17.10 2026-07-22
 * per operator brief 1.6).
 *
 * Design (from operator brief):
 *   • 10-day rolling blocks. Blocks are contiguous: 11-07 → 20-07, 21-07 → 30-07, …
 *   • Current + next block prominent; past blocks read-only (browsable but not editable).
 *   • Per (date × shift): cashier + attendants (each attendant can be marked "(CNG)").
 *   • Weekly-off: per selected person, pick their off-day(s) in the block — grid.
 *   • Copy-forward: single button clones the previous block's assignments and shifts dates.
 *   • Cashier-overlap note: cashier hours span 09-21 / 21-09; overlap is expected.
 *   • Warnings (never blocking): missing cashier, double-assigned same day, below shift minimum.
 *   • First block seeded from the existing HSD/MS 8-hour duty chart (uses `copy_previous`
 *     once the manager saves the first one — the seed is documented + editable).
 *   • Drives Shift-Entry attendee pre-fill via `/api/phase3/roster/for`.
 */
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtDateDMY, todayISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Copy, Plus, Save, X, ArrowLeft, AlertTriangle, Info } from "lucide-react";

const SHIFTS = ["SC-01", "SC-02", "SC-03"];
const SHIFT_LABEL = {
  "SC-01": "Morning · 07:00–15:00",
  "SC-02": "Evening · 15:00–23:00",
  "SC-03": "Night · 23:00–07:00",
};
// Config-managed in a future pass (Payroll Policies · brief 2.7). Defaults here.
const MIN_STAFF = { "SC-01": 11, "SC-02": 11, "SC-03": 6 };

function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
function dayDiff(a, b) {
  return Math.round((new Date(b + "T00:00:00") - new Date(a + "T00:00:00")) / 86400000);
}
function blockStatus(b, today) {
  if (today < b.block_start) return "future";
  if (today > b.block_end) return "past";
  return "current";
}

export default function RosterTab() {
  const [entity, setEntity] = useState("VAS");
  const [blocks, setBlocks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [b, e] = await Promise.all([
        api.get("/phase3/roster/blocks", { params: { entity } }),
        api.get("/payroll/employees", { params: { scope: entity === "VAS" ? "vas" : "corporate" } }),
      ]);
      setBlocks(b.data);
      setEmployees(e.data.filter((x) => x.active !== false));
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  const cashiers = useMemo(() => employees.filter((e) => e.category === "Cashier"), [employees]);
  const attendants = useMemo(
    () => employees.filter((e) => ["Customer Attendant", "Cashier", "Management"].includes(e.category || "")),
    [employees],
  );

  const today = todayISO();
  const sortedBlocks = useMemo(
    () => [...blocks].sort((a, b) => a.block_start.localeCompare(b.block_start)),
    [blocks],
  );
  const currentBlock = sortedBlocks.find((b) => blockStatus(b, today) === "current");
  const nextBlock = sortedBlocks.find((b) => b.block_start > today);
  const pastBlocks = sortedBlocks.filter((b) => b.block_end < today);

  const startNew = () => {
    // Default the new block to start where the last one ended, or today.
    const last = sortedBlocks[sortedBlocks.length - 1];
    const start = last ? addDays(last.block_end, 1) : today;
    setEditing({
      entity,
      block_start: start,
      block_end: addDays(start, 9),
      shifts: buildEmptyShifts(start, 9),
      note: "",
      _isNew: true,
    });
  };

  const openExisting = (b, readOnly = false) => setEditing({
    ...b,
    shifts: b.shifts?.length ? b.shifts : buildEmptyShifts(b.block_start, dayDiff(b.block_start, b.block_end)),
    _readOnly: readOnly,
    _isNew: false,
  });

  const copyPrev = async () => {
    if (!editing) return;
    try {
      const r = await api.post(
        "/phase3/roster/blocks/copy_previous",
        null,
        { params: { entity, block_start: editing.block_start } },
      );
      setEditing({ ...r.data, _isNew: false });
      toast.success("Copied from previous block — edit exceptions and save.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const payload = { ...editing };
      delete payload._isNew;
      delete payload._readOnly;
      await api.post("/phase3/roster/blocks", payload);
      toast.success("Roster block saved.");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <RosterEditor
        block={editing} setBlock={setEditing}
        cashiers={cashiers} attendants={attendants}
        onCancel={() => setEditing(null)}
        onCopyPrev={copyPrev} onSave={save} busy={busy}
      />
    );
  }

  return (
    <div className="mt-3 space-y-3" data-testid="roster-tab-root">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 border-2 border-slate-300 rounded-sm bg-white p-0.5">
          {["VAS", "Corporate"].map((x) => (
            <button key={x} onClick={() => setEntity(x)} data-testid={`roster-entity-${x}`}
                    className={"px-3 py-1.5 text-xs font-bold rounded-sm " + (entity === x ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")}>
              {x}
            </button>
          ))}
        </div>
        <Button onClick={startNew} className="bg-slate-900 text-white h-9 text-xs" data-testid="roster-new-btn">
          <Plus size={12} className="mr-1" />New 10-day block
        </Button>
      </div>

      {busy && <div className="text-xs text-slate-500">Loading…</div>}

      {/* Cashier-overlap note — permanent, at the top so managers see it every time */}
      <Card className="p-2 border-2 border-sky-300 bg-sky-50 rounded-sm flex items-start gap-2" data-testid="roster-overlap-note">
        <Info size={14} className="text-sky-700 mt-0.5 shrink-0" />
        <div className="text-xs text-sky-900">
          <b>Cashier hours overlap the next shift.</b> Cashier is ~09:00–21:00 (day) or 21:00–09:00 (night), so on both morning + evening (or evening + night) the same cashier appears — this is expected, not an anomaly.
        </div>
      </Card>

      {/* Current + Next — the two rows the operator actually cares about */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <BlockCard block={currentBlock} label="Current block" badge="LIVE"
                   onOpen={() => currentBlock && openExisting(currentBlock)}
                   testid="roster-current-block" />
        <BlockCard block={nextBlock} label="Next block" badge="UPCOMING"
                   onOpen={() => nextBlock && openExisting(nextBlock)}
                   emptyCta={{ label: "Build next 10-day block", onClick: startNew }}
                   testid="roster-next-block" />
      </div>

      {/* Past blocks — read-only browsing */}
      {pastBlocks.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Past blocks (read-only)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {pastBlocks.map((b) => (
              <Card key={b.block_id} className="p-2 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50"
                    onClick={() => openExisting(b, true)} data-testid={`roster-past-block-${b.block_id}`}>
                <div className="text-xs font-mono text-slate-500">{b.entity}</div>
                <div className="text-sm font-bold">{fmtDateDMY(b.block_start)} → {fmtDateDMY(b.block_end)}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{b.shifts?.length || 0} shifts</div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {!busy && sortedBlocks.length === 0 && (
        <Card className="p-8 text-center border-2 border-slate-300 border-dashed rounded-sm">
          <div className="font-bold text-slate-800">No roster blocks yet.</div>
          <div className="text-xs text-slate-600 mt-1">Click <b>New 10-day block</b> to create your first one.</div>
        </Card>
      )}
    </div>
  );
}

function BlockCard({ block, label, badge, onOpen, emptyCta, testid }) {
  if (!block) {
    return (
      <Card className="p-4 border-2 border-dashed border-slate-300 rounded-sm bg-slate-50" data-testid={testid}>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
        <div className="text-sm text-slate-500 italic">No block found.</div>
        {emptyCta && (
          <Button onClick={emptyCta.onClick} className="mt-2 h-8 text-xs bg-slate-900 text-white">
            <Plus size={12} className="mr-1" />{emptyCta.label}
          </Button>
        )}
      </Card>
    );
  }
  return (
    <Card className="p-3 border-2 border-slate-900 rounded-sm cursor-pointer hover:bg-slate-50" onClick={onOpen} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
        <span className="text-[10px] font-bold px-2 py-0.5 bg-slate-900 text-white rounded-sm">{badge}</span>
      </div>
      <div className="font-bold text-slate-900">{fmtDateDMY(block.block_start)} → {fmtDateDMY(block.block_end)}</div>
      <div className="text-xs text-slate-600 mt-1">{block.shifts?.length || 0} shifts assigned{block.note ? ` · ${block.note}` : ""}</div>
      <div className="text-[10px] text-slate-500 mt-1">Tap to edit.</div>
    </Card>
  );
}

// -----------------------------------------------------------------------------

function RosterEditor({ block, setBlock, cashiers, attendants, onCancel, onCopyPrev, onSave, busy }) {
  const readOnly = block._readOnly;
  const dates = useMemo(() => {
    const out = [];
    const days = dayDiff(block.block_start, block.block_end);
    for (let i = 0; i <= days; i++) out.push(addDays(block.block_start, i));
    return out;
  }, [block.block_start, block.block_end]);

  const getShift = (d, sc) => block.shifts.find((s) => s.date === d && s.shift_code === sc) || {
    date: d, shift_code: sc, cashier_code: "", attendant_codes: [], cng_attendant_codes: [], weekly_off_codes: [],
  };

  const upsertShift = (d, sc, patch) => {
    if (readOnly) return;
    const idx = block.shifts.findIndex((s) => s.date === d && s.shift_code === sc);
    const base = idx >= 0 ? block.shifts[idx] : {
      date: d, shift_code: sc, cashier_code: "", attendant_codes: [], cng_attendant_codes: [], weekly_off_codes: [],
    };
    const next = { ...base, ...patch };
    const arr = idx >= 0 ? [...block.shifts.slice(0, idx), next, ...block.shifts.slice(idx + 1)] : [...block.shifts, next];
    setBlock({ ...block, shifts: arr });
  };

  const addAttendant = (d, sc, code) => {
    if (!code) return;
    const s = getShift(d, sc);
    if (s.attendant_codes?.includes(code)) return;
    upsertShift(d, sc, { attendant_codes: [...(s.attendant_codes || []), code] });
  };
  const removeAttendant = (d, sc, code) => {
    const s = getShift(d, sc);
    upsertShift(d, sc, {
      attendant_codes: (s.attendant_codes || []).filter((c) => c !== code),
      cng_attendant_codes: (s.cng_attendant_codes || []).filter((c) => c !== code),
    });
  };
  const toggleCNG = (d, sc, code) => {
    const s = getShift(d, sc);
    const isCng = (s.cng_attendant_codes || []).includes(code);
    upsertShift(d, sc, {
      cng_attendant_codes: isCng
        ? (s.cng_attendant_codes || []).filter((c) => c !== code)
        : [...(s.cng_attendant_codes || []), code],
    });
  };

  // Warnings (never block save) — surface in a banner above the grid.
  const warnings = useMemo(() => buildWarnings(block, dates), [block, dates]);

  const empByCode = useMemo(() => Object.fromEntries(attendants.map((a) => [a.code, a])), [attendants]);

  // Everyone touched in this block — used for the weekly-off grid.
  const peopleInBlock = useMemo(() => {
    const codes = new Set();
    block.shifts.forEach((s) => {
      if (s.cashier_code) codes.add(s.cashier_code);
      (s.attendant_codes || []).forEach((c) => codes.add(c));
    });
    return Array.from(codes).map((c) => empByCode[c]).filter(Boolean).sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
  }, [block.shifts, empByCode]);

  const isWeeklyOff = (code, date) => block.shifts.some((s) => s.date === date && (s.weekly_off_codes || []).includes(code));
  const toggleWeeklyOff = (code, date) => {
    // Weekly-off is a day-level marker — write to SC-01 by convention (any shift row for that date works).
    const targetShift = block.shifts.find((s) => s.date === date && s.shift_code === "SC-01") || {
      date, shift_code: "SC-01", cashier_code: "", attendant_codes: [], cng_attendant_codes: [], weekly_off_codes: [],
    };
    const has = (targetShift.weekly_off_codes || []).includes(code);
    upsertShift(date, "SC-01", {
      weekly_off_codes: has
        ? (targetShift.weekly_off_codes || []).filter((c) => c !== code)
        : [...(targetShift.weekly_off_codes || []), code],
    });
  };

  return (
    <div className="mt-3 space-y-3" data-testid="roster-editor">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" onClick={onCancel} size="sm" className="border-slate-300 h-9 text-xs" data-testid="roster-back-btn">
          <ArrowLeft size={12} className="mr-1" />Back
        </Button>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-lg">
            {readOnly ? "Viewing " : "Editing "}
            {fmtDateDMY(block.block_start)} → {fmtDateDMY(block.block_end)}
            {readOnly && <span className="ml-2 text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-sm">READ-ONLY</span>}
          </div>
        </div>
        {!readOnly && (
          <>
            <Button variant="outline" size="sm" onClick={onCopyPrev} className="border-slate-300 h-9 text-xs" data-testid="roster-copy-prev">
              <Copy size={12} className="mr-1" />Copy previous block
            </Button>
            <Button onClick={onSave} disabled={busy} size="sm" className="bg-slate-900 text-white h-9 text-xs" data-testid="roster-save">
              <Save size={12} className="mr-1" />{busy ? "Saving…" : "Save block"}
            </Button>
          </>
        )}
      </div>

      {!readOnly && (
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Block start</label>
          <Input type="date" value={block.block_start}
                 onChange={(e) => { const s = e.target.value; setBlock({ ...block, block_start: s, block_end: addDays(s, 9) }); }}
                 className="w-40 h-9" data-testid="roster-block-start" />
          <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Block end</label>
          <Input type="date" value={block.block_end}
                 onChange={(e) => setBlock({ ...block, block_end: e.target.value })}
                 className="w-40 h-9" data-testid="roster-block-end" />
          <Input placeholder="Note (optional)" value={block.note || ""}
                 onChange={(e) => setBlock({ ...block, note: e.target.value })}
                 className="flex-1 min-w-[140px] h-9" />
        </div>
      )}

      {/* Warnings banner */}
      {warnings.length > 0 && (
        <Card className="p-2 border-2 border-amber-400 bg-amber-50 rounded-sm" data-testid="roster-warnings">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-900 space-y-0.5 flex-1">
              <div className="font-bold">{warnings.length} thing{warnings.length === 1 ? "" : "s"} to double-check (not blocking — save anyway if you know what you&apos;re doing):</div>
              <ul className="list-disc ml-4">
                {warnings.slice(0, 8).map((w, i) => <li key={i} data-testid={`roster-warning-${i}`}>{w}</li>)}
                {warnings.length > 8 && <li>+ {warnings.length - 8} more</li>}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Assignment grid — three shift panels per day */}
      <div className="space-y-2">
        {dates.map((d) => (
          <Card key={d} className="p-2 border-2 border-slate-300 rounded-sm bg-white" data-testid={`roster-day-${d}`}>
            <div className="font-bold text-sm text-slate-900 mb-1">{fmtDateDMY(d)}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {SHIFTS.map((sc) => {
                const s = getShift(d, sc);
                const staffCount = (s.attendant_codes || []).length + (s.cashier_code ? 1 : 0);
                const under = staffCount < MIN_STAFF[sc];
                return (
                  <div key={sc} className="border border-slate-200 rounded-sm p-2 bg-slate-50"
                       data-testid={`roster-cell-${d}-${sc}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700">{SHIFT_LABEL[sc]}</div>
                      <div className={"text-[10px] font-mono " + (under ? "text-red-700 font-bold" : "text-slate-500")}>
                        {staffCount}/{MIN_STAFF[sc]}
                      </div>
                    </div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase mb-0.5">Cashier</div>
                    <select
                      value={s.cashier_code} onChange={(e) => upsertShift(d, sc, { cashier_code: e.target.value })}
                      disabled={readOnly}
                      className="w-full h-8 border border-slate-300 rounded-sm px-1 bg-white text-xs mb-2 disabled:bg-slate-100"
                      data-testid={`roster-cashier-${d}-${sc}`}
                    >
                      <option value="">— cashier —</option>
                      {cashiers.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                    <div className="text-[10px] font-bold text-slate-500 uppercase mb-0.5">Attendants ({s.attendant_codes?.length || 0})</div>
                    <div className="flex flex-wrap gap-1 mb-1">
                      {(s.attendant_codes || []).map((code) => {
                        const emp = empByCode[code];
                        const isCng = (s.cng_attendant_codes || []).includes(code);
                        return (
                          <span key={code}
                                className={"inline-flex items-center gap-1 text-[10px] font-bold rounded-sm border px-1.5 py-0.5 " +
                                  (isCng ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-slate-400 bg-white text-slate-800")}
                                data-testid={`roster-attendant-chip-${d}-${sc}-${code}`}>
                            {emp?.name || code}
                            {!readOnly && (
                              <>
                                <button type="button" onClick={() => toggleCNG(d, sc, code)}
                                        className={"text-[9px] px-1 rounded-sm " + (isCng ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-700 hover:bg-slate-300")}
                                        title="Toggle CNG island"
                                        data-testid={`roster-cng-toggle-${d}-${sc}-${code}`}>
                                  CNG
                                </button>
                                <button type="button" onClick={() => removeAttendant(d, sc, code)}
                                        className="text-slate-500 hover:text-red-600"
                                        data-testid={`roster-remove-attendant-${d}-${sc}-${code}`}>
                                  <X size={10} />
                                </button>
                              </>
                            )}
                          </span>
                        );
                      })}
                    </div>
                    {!readOnly && (
                      <select
                        value=""
                        onChange={(e) => { addAttendant(d, sc, e.target.value); e.target.value = ""; }}
                        className="w-full h-7 border border-slate-300 rounded-sm px-1 bg-white text-[11px]"
                        data-testid={`roster-add-attendant-${d}-${sc}`}
                      >
                        <option value="">+ Add attendant…</option>
                        {attendants
                          .filter((a) => !(s.attendant_codes || []).includes(a.code) && a.code !== s.cashier_code)
                          .map((a) => <option key={a.code} value={a.code}>{a.name} · {a.category?.slice(0, 3) || ""}</option>)}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      {/* Weekly-off grid — per selected person, per day toggle */}
      {peopleInBlock.length > 0 && (
        <Card className="p-3 border-2 border-slate-300 rounded-sm bg-white" data-testid="roster-weekly-off-grid">
          <div className="font-bold text-sm text-slate-900 mb-2">Weekly Off · people × 10 days</div>
          <div className="text-xs text-slate-600 mb-2">Click a cell to mark that person OFF that day. Payroll uses this to detect worked-on-WO days.</div>
          <div className="overflow-x-auto">
            <table className="text-xs w-full border-collapse">
              <thead>
                <tr>
                  <th className="text-left px-1 py-1 border-b border-slate-200">Person</th>
                  {dates.map((d) => (
                    <th key={d} className="text-center px-1 py-1 border-b border-slate-200 font-mono text-[10px]">
                      {new Date(d + "T00:00:00").getDate()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {peopleInBlock.map((p) => (
                  <tr key={p.code} className="border-b border-slate-100">
                    <td className="px-1 py-1 text-xs">{p.name}</td>
                    {dates.map((d) => {
                      const off = isWeeklyOff(p.code, d);
                      return (
                        <td key={d} className="text-center px-1 py-0.5">
                          <button
                            type="button"
                            disabled={readOnly}
                            onClick={() => toggleWeeklyOff(p.code, d)}
                            className={"w-6 h-6 rounded-sm text-[10px] font-bold border " +
                              (off
                                ? "bg-red-500 border-red-600 text-white"
                                : "bg-white border-slate-300 text-slate-400 hover:bg-slate-100")}
                            data-testid={`roster-wo-${p.code}-${d}`}
                            title={off ? "Weekly off — click to clear" : "Set weekly off"}
                          >
                            {off ? "OFF" : ""}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function buildEmptyShifts(start, days) {
  const out = [];
  for (let i = 0; i <= days; i++) {
    const d = addDays(start, i);
    for (const sc of SHIFTS) {
      out.push({ date: d, shift_code: sc, cashier_code: "", attendant_codes: [], cng_attendant_codes: [], weekly_off_codes: [] });
    }
  }
  return out;
}

function buildWarnings(block, dates) {
  const warnings = [];
  // 1. Missing cashier on any shift
  for (const d of dates) {
    for (const sc of SHIFTS) {
      const s = block.shifts.find((x) => x.date === d && x.shift_code === sc);
      if (!s?.cashier_code) {
        warnings.push(`No cashier assigned · ${d} · ${SHIFT_LABEL[sc]}`);
      }
    }
  }
  // 2. Same person assigned to 2 shifts on the same day (excluding overlapping cashier — that's expected)
  for (const d of dates) {
    const perPerson = new Map();
    for (const sc of SHIFTS) {
      const s = block.shifts.find((x) => x.date === d && x.shift_code === sc);
      if (!s) continue;
      const codes = [...(s.attendant_codes || [])];
      for (const c of codes) {
        perPerson.set(c, (perPerson.get(c) || 0) + 1);
      }
    }
    for (const [c, n] of perPerson.entries()) {
      if (n > 1) warnings.push(`${c} is assigned to ${n} shifts on ${d}`);
    }
  }
  // 3. Below shift minimum
  for (const d of dates) {
    for (const sc of SHIFTS) {
      const s = block.shifts.find((x) => x.date === d && x.shift_code === sc);
      const staff = (s?.attendant_codes?.length || 0) + (s?.cashier_code ? 1 : 0);
      if (s && staff > 0 && staff < MIN_STAFF[sc]) {
        warnings.push(`Below minimum (${staff}/${MIN_STAFF[sc]}) · ${d} · ${SHIFT_LABEL[sc]}`);
      }
    }
  }
  return warnings;
}
